"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { Area, AreaChart } from "recharts";
import { ArrowRight, Activity } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Jan",
  impact: 120
}, {
  month: "Feb",
  impact: 135
}, {
  month: "Mar",
  impact: 125
}, {
  month: "Apr",
  impact: 170
}, {
  month: "May",
  impact: 210
}, {
  month: "Jun",
  impact: 290
}];
const chartConfig = {
  impact: {
    label: "Impact Metric",
    color: "var(--chart-2)"
  }
};
export default function ClarityInEveryEntry() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: visualVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="238277" id="clarity-in-every-entry" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/10 border border-secondary/20 w-fit mb-8">
              <Activity className="size-4 text-secondary" />
              <span className="text-xs font-mono font-medium text-secondary uppercase tracking-widest">
                Precision Bookkeeping
              </span>
            </div>
            
            <h1 className="font-default text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tighter text-foreground">
              <span className="bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">Clarity</span> in Every Entry
            </h1>
            
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed font-default">
              LEDGER provides professional bookkeeping and accounting services tailored to small businesses and startups seeking accurate, transparent financial management.
            </p>
            
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="h-12 px-8 rounded-md bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity animate-pulse shadow-lg shadow-primary/20">
                <Link to="#">
                  Get Started
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="h-12 px-8 rounded-md border border-border hover:bg-accent">
                <Link to="/OurServicesPage">
                  Our Solutions
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={visualRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-200 ease-out ${visualVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative rounded-lg overflow-hidden border border-border/50 shadow-4xl bg-card aspect-[4/5] lg:aspect-auto lg:h-[600px] backdrop-blur-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684618/img/abstract_glass_ledger_render.png" alt="Abstract 3D glass ledger with floating data points" className="w-full h-full object-cover opacity-80" />
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
            </div>

            {}
            <Card className="absolute -bottom-6 -left-6 sm:-bottom-10 sm:-left-10 w-[calc(100%-2rem)] sm:w-80 bg-card/80 backdrop-blur-xl border-border/50 shadow-3xl rounded-lg">
              <CardHeader className="pb-2 pt-5 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-default text-sm font-bold text-foreground uppercase tracking-wider">
                    Cash Flow Insight
                  </CardTitle>
                  <span className="text-[10px] font-mono text-secondary bg-secondary/10 px-2 py-0.5 rounded-sm">
                    REAL-TIME
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 pt-0">
                <ChartContainer config={chartConfig} className="h-[100px] w-full mt-2">
                  <AreaChart data={chartData} margin={{
                  top: 5,
                  right: 0,
                  left: 0,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="fillImpact" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-impact)" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="var(--color-impact)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="impact" stroke="var(--color-impact)" fill="url(#fillImpact)" strokeWidth={2.5} animationDuration={2000} />
                  </AreaChart>
                </ChartContainer>
                <div className="mt-3 flex items-end justify-between">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-mono">Monthly Growth</p>
                    <p className="font-default text-2xl font-bold text-foreground leading-none">
                      +141%
                    </p>
                  </div>
                  <div className="h-2 w-2 rounded-lg bg-secondary animate-pulse mb-1" />
                </div>
              </CardContent>
            </Card>
          </div>
          
        </div>
      </div>
    </section>;
}